"use client";

import { Core } from "@faire/design-tokens";
import { HeaderDesktop } from "@faire/retailer-visitor-shared/components/Header/desktop/HeaderDesktop";
import { HeaderMobile } from "@faire/retailer-visitor-shared/components/Header/mobile/HeaderMobile";
import { isMobileApp } from "@faire/retailer-visitor-shared/lib/isMobileApp";
import { PageLoadEvent } from "@faire/retailer-visitor-shared/services/performance/const";
import { PerformanceMarker } from "@faire/retailer-visitor-shared/services/performance/PerformanceMarker";
import {
  useAssignSettingMobileLoggedOutNavUpdate,
  useIsEligibleForMobileLoggedOutNavUpdate,
  useShouldShowUpdatedMobileSearch,
} from "@faire/retailer-visitor-shared/settings/useSettingLoggedOutNavUpdate";
import { isPrerender } from "@faire/web--source/common/user-agent/isPrerender";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { EmbeddedLink } from "@faire/web--source/ui/EmbeddedLink";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import * as React from "react";
import { useEffect } from "react";
import styled from "styled-components";

import { useHeaderState } from "./useHeaderState";

export const Header = () => {
  const assignSettingMobileLoggedOutNavUpdate =
    useAssignSettingMobileLoggedOutNavUpdate();
  const isEligibleForMobileLoggedOutNavUpdate =
    useIsEligibleForMobileLoggedOutNavUpdate();

  useEffect(() => {
    if (isEligibleForMobileLoggedOutNavUpdate) {
      assignSettingMobileLoggedOutNavUpdate();
    }
  }, [
    assignSettingMobileLoggedOutNavUpdate,
    isEligibleForMobileLoggedOutNavUpdate,
  ]);

  useEffect(() => {
    PerformanceMarker.get().mark(PageLoadEvent.firstPaint);
  }, []);

  const { isShowingMobileSearch } = useHeaderState();
  const isUpdatedNavSearchExperience = useShouldShowUpdatedMobileSearch();

  if (isShowingMobileSearch && isUpdatedNavSearchExperience) {
    return null;
  }

  return (
    <>
      <HeaderWithPrerenderHack />

      {/* Link to provide customers with only a keyboard a way to skip over the header links */}
      <SkipToMainLink tabIndex={1} linkTo="#main">
        <StrictLocalMsg
          defaultMessage="Skip to main content"
          description={{
            text: "Button to skip the current page and jump to main content directly",
          }}
        />
      </SkipToMainLink>
    </>
  );
};

const HeaderWithPrerenderHack = () => {
  /**
   * Prerender only caches the HTML for one viewport size. This means that if
   * the crawler is on mobile, it will see the cached HTML for desktop. This
   * will affect the rating of the page in terms of mobile friendliness.
   * To avoid this, we render the both headers for prerender, and then use the
   * CSS media query to hide the one that is not needed. This has performance
   * implications, so we should only do this for prerender.
   */
  if (isPrerender()) {
    return (
      <>
        <StyledHeaderDesktop />
        <MaybeHeaderMobile />
      </>
    );
  }

  /**
   * If we are not in pre-render, we can use the viewport hook to determine
   * which header to render.
   */
  return <MobileOrDesktopHeader />;
};

const MobileOrDesktopHeader = () => {
  const { isDesktopAndAbove } = useViewport();
  return isDesktopAndAbove ? <StyledHeaderDesktop /> : <MaybeHeaderMobile />;
};

const MaybeHeaderMobile = () => {
  if (isMobileApp()) {
    return null;
  }
  return <StyledHeaderMobile />;
};

// Note StyledHeaderDesktop and StyledHeaderMobile are defined to avoid viewport
//  mismatch between server and client. Please do not remove them unless you
//  know what you are doing.
const StyledHeaderDesktop = styled(HeaderDesktop)`
  ${responsiveMediaQueries.tabletAndBelow} {
    display: none;
  }
`;

const StyledHeaderMobile = styled(HeaderMobile)`
  ${responsiveMediaQueries.desktopAndAbove} {
    display: none;
  }
`;

const SkipToMainLink = styled(EmbeddedLink)`
  background: ${Core.surface.inverse};
  left: 50%;
  padding: 8px;
  position: fixed;
  top: 0;
  transform: translateY(-100%);
  transition: transform 0.3s;
  z-index: 100;
  &:focus {
    transform: translateY(0%);
  }
  @media print {
    display: none;
  }
`;
